/**
 * Domínio: descrição de um evento de PLANTÃO na trilha de auditoria — GUPM-273.
 *
 * O payload do `audit_log` é `to_jsonb(NEW)` (ver `audit_changes()` em
 * `0001_initial_schema.sql`): o snapshot da linha inteira, não um diff. Logo o
 * UPDATE de encerramento carrega também os campos escritos na abertura.
 *
 * A regra desta função, e a razão de ela existir:
 *
 *   campo de abertura pertence ao evento INSERT;
 *   campo de encerramento pertence ao evento UPDATE;
 *   nunca cruzar.
 *
 * Renderizar "o que estiver presente no payload" — o que a tela fazia antes —
 * põe a atestação de quem abriu debaixo do nome de quem encerrou. Numa tela
 * intitulada "Trilha de Auditoria — ANM Res. 95/2022", isso é uma atestação
 * atribuída a quem não a fez.
 *
 * TS puro: sem React, sem Supabase, sem DOM.
 */

import type { ChecklistResponse } from '@/domain/shift-handover/types';

/** Momento do ciclo do plantão a que o evento de auditoria pertence. */
export type HandoverMoment = 'abertura' | 'passagem' | 'encerramento';

export interface HandoverTexto {
  /** Rótulo em pt-BR — nunca a chave crua do payload. */
  label: string;
  value: string;
}

export interface HandoverAuditView {
  moment: HandoverMoment;
  /** Rótulo da linha na coluna "Entidade". */
  label: string;
  /** Respostas a exibir NESTE evento; vazio quando não há o que exibir. */
  checklist: ChecklistResponse[];
  /** Rótulo do bloco de checklist; `null` quando não há checklist a exibir. */
  checklistLabel: string | null;
  /** Textos escritos que pertencem a ESTE evento. */
  textos: HandoverTexto[];
}

/** `end_kind` gravado a partir do GUPM-273. Linha anterior a ele não tem. */
type EndKind = 'rendicao' | 'solo' | 'admin';

const LABEL_POR_END_KIND: Record<EndKind, string> = {
  rendicao: 'Encerramento por rendição',
  solo: 'Encerramento pelo plantonista',
  admin: 'Destrave administrativo',
};

const CHECKLIST_LABEL: Record<HandoverMoment, string> = {
  abertura: 'Checklist da abertura',
  passagem: 'Checklist da passagem',
  encerramento: 'Checklist de saída',
};

function asRecord(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  return payload as Record<string, unknown>;
}

function texto(label: string, value: unknown): HandoverTexto[] {
  if (typeof value !== 'string' || value.trim().length === 0) return [];
  return [{ label, value }];
}

/** Respostas de checklist sob uma chave específica do payload. Nunca lança. */
function respostas(row: Record<string, unknown>, key: string): ChecklistResponse[] {
  const items = row[key];
  if (!Array.isArray(items)) return [];
  return items.filter(
    (i): i is ChecklistResponse =>
      !!i && typeof i === 'object' && 'item_id' in i && 'answer' in i,
  );
}

/**
 * Como um encerramento anterior ao GUPM-273 é rotulado: `end_kind` não existe
 * nessas linhas, então vale a regra que o GUPM-272 documentou —
 * `ended_by = operator_in_id` é o próprio plantonista saindo; diferente disso é
 * destrave administrativo.
 *
 * Decidido por ID e não por nome de propósito: dois homônimos na escala fariam
 * um destrave virar "encerrou o próprio plantão".
 */
function rotuloDeEncerramento(row: Record<string, unknown>): string {
  const endKind = row.end_kind;
  if (typeof endKind === 'string' && endKind in LABEL_POR_END_KIND) {
    return LABEL_POR_END_KIND[endKind as EndKind];
  }
  return row.ended_by === row.operator_in_id
    ? LABEL_POR_END_KIND.solo
    : LABEL_POR_END_KIND.admin;
}

/**
 * Descreve um evento de `shift_handovers` do `audit_log`.
 *
 * Devolve `null` quando o evento não é descritível como momento do ciclo do
 * plantão (payload malformado, DELETE, ou UPDATE que não encerrou nada) — o
 * chamador cai no detalhamento genérico de payload em vez de inventar um
 * encerramento que não houve.
 */
export function describeHandoverAudit(entry: {
  action: string;
  payload: unknown;
}): HandoverAuditView | null {
  const row = asRecord(entry.payload);
  if (!row) return null;

  if (entry.action === 'INSERT') {
    // `operator_out_id` nulo não é "não sei quem saiu": é "não houve passagem".
    const moment: HandoverMoment = row.operator_out_id == null ? 'abertura' : 'passagem';
    const checklist = respostas(row, 'checklist_items');
    return {
      moment,
      label: moment === 'abertura' ? 'Abertura de plantão' : 'Passagem de plantão',
      checklist,
      checklistLabel: checklist.length > 0 ? CHECKLIST_LABEL[moment] : null,
      textos: texto('Observação', row.observation),
    };
  }

  if (entry.action === 'UPDATE') {
    // UPDATE numa linha AINDA ABERTA não é encerramento e não vai ser descrito
    // como um.
    //
    // LIMITE CONHECIDO (GUPM-288) — este guarda distingue "a linha está
    // fechada", não "este UPDATE fechou a linha". O payload do `audit_changes()`
    // é só `to_jsonb(NEW)`, sem o estado anterior: um UPDATE posterior numa
    // linha já encerrada (soft-delete, correção administrativa, backfill de
    // conferência) chega aqui com `ended_at` preenchido de antes e sai rotulado
    // como o encerramento que não houve.
    //
    // É a terceira aparição do mesmo defeito neste card — campo que muda de
    // FREQUÊNCIA engole o guarda escrito quando ele era exceção. "1 linha = 1
    // turno" torna `ended_at IS NOT NULL` a regra, e não mais a marca do evento
    // de fechamento.
    //
    // Não consertado aqui de propósito: hoje NENHUM caminho do app faz UPDATE em
    // linha encerrada (o app só lê `deleted_at`; as RPCs escrevem na linha
    // aberta), e no release o backfill não toca linha nenhuma em produção — há 1
    // linha, sem `ended_at`. O conserto certo mexe no que a trilha GRAVA
    // (estado anterior no payload, ou trigger dedicado), e isso é migration em
    // tabela append-only de conformidade — não entra na véspera do release.
    if (row.ended_at == null) return null;
    const checklist = respostas(row, 'end_checklist_items');
    return {
      moment: 'encerramento',
      label: rotuloDeEncerramento(row),
      checklist,
      checklistLabel: checklist.length > 0 ? CHECKLIST_LABEL.encerramento : null,
      textos: texto('Motivo do encerramento', row.end_reason),
    };
  }

  return null;
}
